import { useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Lock,
  User,
  ShieldCheck,
  Eye,
  EyeOff,
  Stethoscope,
} from "lucide-react";
import toast from "../utils/toast";
import ATHLogo from "../assets/ATHLogo.jpg";

const Login = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
    remember: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = await login({
      username: form.username,
      password: form.password,
      remember: form.remember,
    });

    if (result.success) {
      toast.success(result.message);
      navigate("/dashboard");
    } else {
      toast.error(result.message);
    }
  };

  return (
    <div className="w-full h-screen flex bg-slate-50 font-sans overflow-hidden">
      
      {/* LEFT SIDE: ONLY LOGO & NAME */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1b2559] text-white flex-col items-center justify-center p-12 relative">
        <div className="flex flex-col items-center text-center gap-6">
          <div className="p-4 bg-white rounded-3xl shadow-2xl border border-slate-100">
            <img
              src={ATHLogo}
              alt="Asha The Hope Logo"
              className="h-28 w-auto object-contain"
            />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-wider text-white uppercase">
              Asha The Hope
            </h1>
            <p className="text-xs font-bold text-slate-300 tracking-[0.25em] mt-2 uppercase">
              Diagnostic & Imaging LLP
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE: LOGIN FORM WITH BORDER CARD */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 shadow-xl space-y-8">
          
          {/* Mobile Header Logo (Visible on Small Screens) */}
          <div className="text-center lg:hidden mb-6">
            <div className="inline-block p-2 bg-white rounded-2xl shadow-md border border-slate-100 mb-3">
              <img
                src={ATHLogo}
                alt="Asha The Hope Logo"
                className="h-14 w-auto mx-auto object-contain"
              />
            </div>
            <h2 className="text-xl font-black text-[#1b2559]">ASHA THE HOPE</h2>
            <p className="text-xs font-bold text-slate-500 tracking-wider">
              DIAGNOSTIC & IMAGING LLP
            </p>
          </div>

          {/* Form Header */}
          <div>
            <div className="inline-flex p-3 rounded-2xl bg-[#1b2559]/5 text-[#1b2559] mb-4">
              <Stethoscope size={24} />
            </div>
            <h2 className="text-3xl font-extrabold text-[#1b2559] tracking-tight">
              Sign In
            </h2>
          </div>

          {/* Form Input Elements */}
          <form onSubmit={handleSubmit} className="space-y-5">
            
            {/* USERNAME FIELD */}
            <div>
              <label className="block text-xs font-black uppercase tracking-wider text-[#1b2559] mb-2">
                User ID / Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <User size={18} />
                </div>
                <input
                  type="text"
                  name="username"
                  required
                  value={form.username}
                  onChange={handleChange}
                  placeholder="Enter your username"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[#1b2559] text-sm font-bold focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* PASSWORD FIELD */}
            <div>
              <label className="block text-xs font-black uppercase tracking-wider text-[#1b2559] mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[#1b2559] text-sm font-bold focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* SUBMIT BUTTON */}
            <button
              type="submit"
              className="w-full py-3.5 px-4 bg-[#1b2559] hover:bg-[#12193b] text-white font-bold rounded-xl shadow-lg shadow-[#1b2559]/20 flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]"
            >
              <ShieldCheck size={20} />
              Sign In to Account
            </button>
          </form>

        </div>
      </div>

    </div>
  );
};

export default Login;